<template>
  <modal
    name="steward-report"
    height="90%"
    width="95%"
    :scrollable="true"
    :adaptive="true"
    class="modal-dialog"
    @closed="close"
  >
    <div class="modal-content o-common-modal-content">
      <div class="modal-header text-center">
        <button type="button" class="close" @click="close">
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
        <h4 class="modal-title">Steward Report — #{{ job.job_number }} {{ job.title }}</h4>
      </div>

      <div class="modal-body" v-loading="uploading">

        <!-- ═══════════ HOME VIEW ═══════════ -->
        <div v-if="view === 'home'">
          <div class="sr-actions">
            <button class="c-solid-btn c-solid-btn--green sr-action-btn" @click="startAudio">
              <i class="fa fa-microphone" aria-hidden="true"></i> Record Audio
            </button>
            <button class="c-solid-btn c-solid-btn--green sr-action-btn" @click="view = 'camera-prompt'">
              <i class="fa fa-camera" aria-hidden="true"></i> Camera
            </button>
            <button class="c-solid-btn c-solid-btn--green sr-action-btn" @click="startTextEditor">
              <i class="fa fa-file-text-o" aria-hidden="true"></i> Text Editor
            </button>
            <button class="c-solid-btn c-solid-btn--green sr-action-btn" @click="triggerFileUpload">
              <i class="fa fa-upload" aria-hidden="true"></i> Upload
            </button>
            <input
              ref="fileInput"
              type="file"
              multiple
              style="display: none;"
              @change="handleFileUpload"
            />
          </div>

          <!-- Upload progress -->
          <div v-if="uploadProgress !== null" style="margin: 15px 0;">
            <div class="progress">
              <div
                class="progress-bar progress-bar-success"
                role="progressbar"
                :style="{ width: uploadProgress + '%' }"
              >{{ uploadProgress }}%</div>
            </div>
          </div>

          <!-- File list -->
          <div style="margin-top: 20px;">
            <h5 style="margin-bottom: 10px;">Files</h5>
            <div v-if="loadingFiles" class="text-center" style="padding: 20px;">
              <i class="fa fa-spinner fa-spin"></i> Loading files...
            </div>
            <table v-else-if="files.length" class="table table-bordered table-striped">
              <thead class="sr-thead-dark">
                <tr>
                  <th class="sr-sortable" @click="toggleSort('name')">
                    Name <i v-if="sortKey === 'name'" :class="sortDir === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc'" aria-hidden="true"></i>
                    <i v-else class="fa fa-sort" aria-hidden="true" style="opacity: 0.4;"></i>
                  </th>
                  <th class="sr-sortable" style="width: 80px;" @click="toggleSort('type')">
                    Type <i v-if="sortKey === 'type'" :class="sortDir === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc'" aria-hidden="true"></i>
                    <i v-else class="fa fa-sort" aria-hidden="true" style="opacity: 0.4;"></i>
                  </th>
                  <th class="sr-sortable" style="width: 130px;" @click="toggleSort('steward_name')">
                    Created By <i v-if="sortKey === 'steward_name'" :class="sortDir === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc'" aria-hidden="true"></i>
                    <i v-else class="fa fa-sort" aria-hidden="true" style="opacity: 0.4;"></i>
                  </th>
                  <th class="sr-sortable" style="width: 150px;" @click="toggleSort('created_at')">
                    Date <i v-if="sortKey === 'created_at'" :class="sortDir === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc'" aria-hidden="true"></i>
                    <i v-else class="fa fa-sort" aria-hidden="true" style="opacity: 0.4;"></i>
                  </th>
                  <th style="width: 120px;">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="f in sortedFiles" :key="f.id">
                  <td style="white-space: nowrap;">{{ f.name }}</td>
                  <td class="text-center">
                    <i :class="typeIcon(f.type)" aria-hidden="true" :title="f.type"></i>
                  </td>
                  <td>{{ f.steward_name || '--' }}</td>
                  <td>{{ formatDate(f.created_at) }}</td>
                  <td class="text-center">
                    <a href="javascript:void(0)" @click="previewFile(f)" title="Preview" style="margin-right: 10px;">
                      <i class="fa fa-eye" aria-hidden="true"></i>
                    </a>
                    <a v-if="isEditableFile(f)" href="javascript:void(0)" @click="editExistingFile(f)" title="Edit">
                      <i class="fa fa-pencil-square-o" aria-hidden="true" style="color: #67c23a;"></i>
                    </a>
                  </td>
                </tr>
              </tbody>
            </table>
            <p v-else style="color: #999; text-align: center;">No files uploaded yet.</p>
          </div>
        </div>

        <!-- ═══════════ AUDIO RECORDING ═══════════ -->
        <div v-if="view === 'audio'">
          <h5>Record Audio</h5>
          <div style="margin-bottom: 10px;">
            <label>Microphone:</label>
            <select v-model="selectedAudioDevice" class="form-control" style="display: inline-block; width: auto; margin-left: 8px;">
              <option v-for="d in audioDevices" :key="d.deviceId" :value="d.deviceId">{{ d.label || 'Microphone ' + d.deviceId.slice(0, 8) }}</option>
            </select>
          </div>

          <!-- Waveform indicator -->
          <div v-if="audioRecording" class="sr-waveform">
            <canvas ref="waveCanvas" width="400" height="60" style="width: 100%; height: 60px;"></canvas>
            <p style="color: #c0392b; margin-top: 5px;"><i class="fa fa-circle" aria-hidden="true"></i> Recording... {{ recordingTime }}</p>
          </div>

          <div class="sr-btn-row">
            <button v-if="!audioRecording" class="c-solid-btn c-solid-btn--green" @click="startRecording">
              <i class="fa fa-circle" aria-hidden="true" style="color: red;"></i> Start Recording
            </button>
            <button v-if="audioRecording" class="c-solid-btn c-solid-btn--green" @click="stopRecording">
              <i class="fa fa-stop" aria-hidden="true"></i> Stop
            </button>
            <button class="c-solid-btn" @click="cancelMedia">
              <i class="fa fa-arrow-left" aria-hidden="true"></i> Back
            </button>
          </div>
        </div>

        <!-- ═══════════ CAMERA PROMPT ═══════════ -->
        <div v-if="view === 'camera-prompt'">
          <h5>Camera</h5>
          <p>What would you like to capture?</p>
          <div class="sr-actions">
            <button class="c-solid-btn c-solid-btn--green sr-action-btn" @click="startCamera('photo')">
              <i class="fa fa-camera" aria-hidden="true"></i> Photo
            </button>
            <button class="c-solid-btn c-solid-btn--green sr-action-btn" @click="startCamera('video')">
              <i class="fa fa-video-camera" aria-hidden="true"></i> Video
            </button>
          </div>
          <div class="sr-btn-row">
            <button class="c-solid-btn" @click="view = 'home'">
              <i class="fa fa-arrow-left" aria-hidden="true"></i> Back
            </button>
          </div>
        </div>

        <!-- ═══════════ CAMERA PHOTO ═══════════ -->
        <div v-if="view === 'camera-photo'">
          <h5>{{ capturedPhotoUrl ? 'Review Photo' : 'Take Photo' }}</h5>

          <!-- Live camera feed (hidden after capture) -->
          <div v-if="!capturedPhotoUrl" class="sr-camera-container sr-media-fit">
            <video ref="cameraVideo" autoplay playsinline muted></video>
          </div>
          <canvas ref="photoCanvas" style="display: none;"></canvas>

          <!-- Captured photo preview -->
          <div v-if="capturedPhotoUrl" class="sr-photo-review sr-media-fit text-center">
            <img :src="capturedPhotoUrl" />
          </div>

          <!-- Buttons: capture mode -->
          <div v-if="!capturedPhotoUrl" class="sr-btn-row">
            <button class="c-solid-btn c-solid-btn--green" @click="capturePhoto">
              <i class="fa fa-camera" aria-hidden="true"></i> Capture
            </button>
            <button class="c-solid-btn" @click="cancelMedia">
              <i class="fa fa-times" aria-hidden="true"></i> Cancel
            </button>
          </div>

          <!-- Buttons: review mode (Accept / Retake / Cancel) -->
          <div v-if="capturedPhotoUrl" class="sr-btn-row sr-photo-actions">
            <button class="c-solid-btn c-solid-btn--green" @click="uploadCapturedPhoto">
              <i class="fa fa-check" aria-hidden="true"></i> Accept
            </button>
            <button class="c-solid-btn c-solid-btn--orange" @click="retakePhoto">
              <i class="fa fa-refresh" aria-hidden="true"></i> Retake
            </button>
            <button class="c-solid-btn c-solid-btn--red" @click="cancelMedia">
              <i class="fa fa-times" aria-hidden="true"></i> Cancel
            </button>
          </div>
        </div>

        <!-- ═══════════ CAMERA VIDEO ═══════════ -->
        <div v-if="view === 'camera-video'">
          <h5>Record Video</h5>

          <!-- Quality selector (before recording starts) -->
          <div v-if="!videoRecording" style="margin-bottom: 10px;">
            <label style="margin-right: 8px;">Quality:</label>
            <select v-model="videoQuality" class="form-control" style="display: inline-block; width: auto;">
              <option v-for="q in videoQualityOptions" :key="q.key" :value="q.key">
                {{ q.label }} — {{ q.maxTime }} min, ~{{ q.sizeMbPerMin }} MB/min ({{ q.maxSizeMb }} MB limit)
              </option>
            </select>
          </div>

          <div class="sr-camera-container sr-media-fit sr-video-wrapper">
            <canvas ref="videoOverlayCanvas" class="sr-video-canvas"></canvas>
            <video ref="cameraVideoRec" autoplay playsinline muted style="display: none;"></video>
          </div>

          <!-- Recording status: countdown + size -->
          <div v-if="videoRecording" style="margin-top: 8px;">
            <div class="sr-rec-status">
              <span class="sr-rec-dot"><i class="fa fa-circle" aria-hidden="true"></i></span>
              <span>{{ recordingTime }}</span>
              <span class="sr-rec-sep">|</span>
              <span :style="{ color: videoTimeRemaining <= 30 ? '#c0392b' : '#666' }">
                {{ videoCountdown }} remaining
              </span>
              <span class="sr-rec-sep">|</span>
              <span>{{ videoSizeMb }} MB / {{ selectedQuality.maxSizeMb }} MB</span>
            </div>
            <!-- Progress bar showing time used -->
            <div class="progress" style="height: 6px; margin-top: 6px;">
              <div
                class="progress-bar"
                :class="videoTimePercent > 85 ? 'progress-bar-danger' : videoTimePercent > 60 ? 'progress-bar-warning' : 'progress-bar-success'"
                :style="{ width: videoTimePercent + '%' }"
              ></div>
            </div>
          </div>

          <div class="sr-btn-row">
            <button v-if="!videoRecording" class="c-solid-btn c-solid-btn--green" @click="startVideoRecording">
              <i class="fa fa-circle" aria-hidden="true" style="color: red;"></i> Start Recording
            </button>
            <button v-if="videoRecording" class="c-solid-btn c-solid-btn--green" @click="stopVideoRecording">
              <i class="fa fa-stop" aria-hidden="true"></i> Stop
            </button>
            <button class="c-solid-btn" @click="cancelMedia">
              <i class="fa fa-arrow-left" aria-hidden="true"></i> Back
            </button>
          </div>
        </div>

        <!-- ═══════════ TEXT EDITOR ═══════════ -->
        <div v-if="view === 'editor'" class="sr-editor-section">
          <h5>{{ editingFile ? 'Editing: ' + editingFile.name : 'Steward Report Editor' }}</h5>
          <textarea ref="editorArea" id="sr-editor" style="width: 100%; min-height: 300px;"></textarea>
          <div class="sr-btn-row">
            <button class="c-solid-btn c-solid-btn--green" @click="saveDocument(false)">
              <i class="fa fa-save" aria-hidden="true"></i> Save
            </button>
            <button class="c-solid-btn c-solid-btn--green" @click="saveDocument(true)">
              <i class="fa fa-save" aria-hidden="true"></i> Save As...
            </button>
            <button class="c-solid-btn" @click="cancelEditor">
              <i class="fa fa-arrow-left" aria-hidden="true"></i> Back
            </button>
          </div>
        </div>

        <!-- ═══════════ PREVIEW ═══════════ -->
        <div v-if="view === 'preview'">
          <h5>{{ previewingFile ? previewingFile.name : 'Preview' }}</h5>
          <div v-if="previewLoading" class="text-center" style="padding: 40px;">
            <i class="fa fa-spinner fa-spin fa-2x"></i>
          </div>

          <div v-if="previewBlobUrl && previewingFile">
            <!-- Image -->
            <div v-if="previewingFile.mime_type && previewingFile.mime_type.indexOf('image/') === 0" class="text-center">
              <img :src="previewBlobUrl" :alt="previewingFile.name" style="max-width: 100%; max-height: 500px;" />
            </div>
            <!-- Audio -->
            <div v-else-if="previewingFile.mime_type && previewingFile.mime_type.indexOf('audio/') === 0" class="text-center" style="padding: 40px;">
              <i class="fa fa-file-audio-o" aria-hidden="true" style="font-size: 64px; color: #F4B400; display: block; margin-bottom: 20px;"></i>
              <audio :src="previewBlobUrl" controls style="width: 100%; max-width: 500px;"></audio>
            </div>
            <!-- Video -->
            <div v-else-if="previewingFile.mime_type && previewingFile.mime_type.indexOf('video/') === 0" class="text-center">
              <video :src="previewBlobUrl" controls style="width: 100%; max-height: 500px;"></video>
            </div>
            <!-- PDF -->
            <div v-else-if="previewingFile.mime_type === 'application/pdf'">
              <iframe :src="previewBlobUrl" style="width: 100%; height: 500px; border: none;"></iframe>
            </div>
            <!-- HTML (by mime or extension) -->
            <div v-else-if="previewingFile.mime_type === 'text/html' || (previewingFile.name && previewingFile.name.match(/\.html?$/i))">
              <iframe :src="previewBlobUrl" style="width: 100%; height: 500px; border: none;"></iframe>
            </div>
            <!-- Text/plain -->
            <div v-else-if="previewingFile.mime_type && previewingFile.mime_type.indexOf('text/') === 0">
              <pre v-if="previewTextContent !== null" class="sr-preview-text">{{ previewTextContent }}</pre>
            </div>
            <!-- Other -->
            <div v-else class="text-center" style="padding: 40px;">
              <i class="fa fa-file-o" aria-hidden="true" style="font-size: 64px; color: #ccc;"></i>
              <p style="margin-top: 16px; color: #999;">Preview is not available for this file type.</p>
            </div>
          </div>

          <div v-if="previewError" class="text-center" style="padding: 20px; color: #c0392b;">
            <i class="fa fa-exclamation-triangle" aria-hidden="true"></i> Failed to load preview.
          </div>

          <div class="sr-btn-row">
            <button class="c-solid-btn" @click="closePreview">
              <i class="fa fa-arrow-left" aria-hidden="true"></i> Back
            </button>
          </div>
        </div>

      </div>

      <div class="modal-footer text-center">
        <a href="javascript:void(0)" @click="close" class="c-solid-btn">Close</a>
      </div>
    </div>
  </modal>
</template>

<script>
import { mapGetters } from 'vuex';
import { getApiBase, safeSetXhrHeaders } from '../../global.js';

// Register CKEditor timestamp plugin (idempotent)
if (typeof CKEDITOR !== 'undefined' && !CKEDITOR.plugins.registered.inserttimestamp) {
  CKEDITOR.plugins.add('inserttimestamp', {
    init: function (editor) {
      editor.addCommand('insertTimestamp', {
        exec: function (ed) {
          var now = new Date();
          var ts = ('0' + (now.getMonth() + 1)).slice(-2) + '/' +
            ('0' + now.getDate()).slice(-2) + '/' +
            now.getFullYear() + ' ' +
            ('0' + now.getHours()).slice(-2) + ':' +
            ('0' + now.getMinutes()).slice(-2) + ':' +
            ('0' + now.getSeconds()).slice(-2);
          ed.insertHtml('<strong>[' + ts + ']</strong>&nbsp;');
        }
      });
      editor.ui.addButton('InsertTimestamp', {
        label: 'Insert Date/Time',
        command: 'insertTimestamp',
        toolbar: 'insert',
        icon: 'about:blank'
      });
    }
  });
}

function ckEditorConfig() {
  return {
    height: '45vh',
    extraPlugins: 'inserttimestamp',
    toolbarGroups: [
      { name: 'clipboard', groups: ['clipboard', 'undo'] },
      { name: 'basicstyles', groups: ['basicstyles', 'cleanup'] },
      { name: 'paragraph', groups: ['list', 'indent', 'blocks', 'align'] },
      { name: 'links' },
      { name: 'insert' },
      { name: 'styles' },
      { name: 'colors' },
      { name: 'tools' },
    ],
    removeButtons: 'Cut,Copy,Paste,Anchor,Subscript,Superscript',
  };
}

export default {
  name: 'StewardReportModal',
  props: {
    job: { type: Object, required: true },
  },
  data: function () {
    return {
      view: 'home',
      files: [],
      loadingFiles: false,
      uploading: false,
      uploadProgress: null,

      // Audio
      audioDevices: [],
      selectedAudioDevice: null,
      audioRecording: false,
      mediaRecorder: null,
      audioChunks: [],
      audioStream: null,
      audioAnalyser: null,
      waveAnimFrame: null,
      recordingTimer: null,
      recordingSeconds: 0,

      // Camera
      cameraStream: null,
      capturedPhotoUrl: null,
      capturedPhotoBlob: null,
      videoRecording: false,
      videoMediaRecorder: null,
      videoChunks: [],
      videoRecordingBytes: 0,
      videoQuality: 'medium',
      overlayAnimFrame: null,

      // Text editor
      editorInstance: null,

      // Preview
      previewingFile: null,
      previewBlobUrl: null,
      previewLoading: false,
      previewError: false,
      previewTextContent: null,

      // Editing existing file
      editingFile: null,

      // Sort
      sortKey: 'created_at',
      sortDir: 'desc',
    };
  },
  computed: {
    ...mapGetters({
      userToken: 'userToken',
      userRole: 'userRole',
      // #1470 — canonical (top-level users.role_id), used only on the
      // cs3-context-role header so SAs identify as SA in logs and ALB
      // routing regardless of the per-tenant role they're assuming.
      userCanonicalRole: 'userCanonicalRole',
      userClientID: 'userClientID',
      employeeId: 'employeeId',
      userFullName: 'userFullName',
    }),
    apiHeaders: function () {
      return {
        'Authorization': this.userToken,
        'cs3-context-role': this.userCanonicalRole,
        'cs3-context-clientId': this.userClientID,
        'cs3-context-employeeId': this.employeeId,
      };
    },
    baseUrl: function () {
      return getApiBase();
    },
    stewardName: function () {
      if (this.job.employee && this.job.employee.employable) {
        var emp = this.job.employee.employable;
        return (emp.first_name + ' ' + (emp.last_name || '')).trim();
      }
      return this.userFullName || 'Steward';
    },
    videoQualityOptions: function () {
      // nginx limit is 20 MB
      var limitMb = 20;
      return [
        { key: 'low',    label: 'Low (480p)',    width: 640,  height: 480,  bps: 500000,  sizeMbPerMin: 1,   maxSizeMb: limitMb, maxTime: Math.floor(limitMb / 1)   },
        { key: 'medium', label: 'Medium (720p)',  width: 1280, height: 720,  bps: 1500000, sizeMbPerMin: 2.5, maxSizeMb: limitMb, maxTime: Math.floor(limitMb / 2.5) },
        { key: 'high',   label: 'High (1080p)',   width: 1920, height: 1080, bps: 4000000, sizeMbPerMin: 5,   maxSizeMb: limitMb, maxTime: Math.floor(limitMb / 5)   },
      ];
    },
    selectedQuality: function () {
      var self = this;
      return this.videoQualityOptions.find(function (q) { return q.key === self.videoQuality; }) || this.videoQualityOptions[1];
    },
    videoMaxSeconds: function () {
      return this.selectedQuality.maxTime * 60;
    },
    videoTimeRemaining: function () {
      return Math.max(0, this.videoMaxSeconds - this.recordingSeconds);
    },
    videoCountdown: function () {
      var rem = this.videoTimeRemaining;
      var mins = Math.floor(rem / 60);
      var secs = rem % 60;
      return (mins < 10 ? '0' : '') + mins + ':' + (secs < 10 ? '0' : '') + secs;
    },
    videoTimePercent: function () {
      if (!this.videoMaxSeconds) return 0;
      return Math.min(100, Math.round(this.recordingSeconds / this.videoMaxSeconds * 100));
    },
    videoSizeMb: function () {
      return (this.videoRecordingBytes / (1024 * 1024)).toFixed(1);
    },
    recordingTime: function () {
      var mins = Math.floor(this.recordingSeconds / 60);
      var secs = this.recordingSeconds % 60;
      return (mins < 10 ? '0' : '') + mins + ':' + (secs < 10 ? '0' : '') + secs;
    },
    sortedFiles: function () {
      var key = this.sortKey;
      var dir = this.sortDir;
      return this.files.slice().sort(function (a, b) {
        var va = (a[key] || '').toString().toLowerCase();
        var vb = (b[key] || '').toString().toLowerCase();
        if (va < vb) return dir === 'asc' ? -1 : 1;
        if (va > vb) return dir === 'asc' ? 1 : -1;
        return 0;
      });
    },
  },
  mounted: function () {
    this.$modal.show('steward-report');
    this.loadFiles();
  },
  beforeDestroy: function () {
    this.cleanupMedia();
    if (this.previewBlobUrl) {
      window.URL.revokeObjectURL(this.previewBlobUrl);
    }
    if (this.editorInstance) {
      try { this.editorInstance.destroy(); } catch (e) { /* noop */ }
    }
  },
  methods: {

    // ──────────── SORT ────────────

    toggleSort: function (key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortDir = 'asc';
      }
    },

    // ──────────── API HELPERS ────────────

    loadFiles: function () {
      var self = this;
      self.loadingFiles = true;
      var jobId = self.job.id;

      var xhr = new XMLHttpRequest();
      xhr.open('GET', self.baseUrl + 'steward-reports/' + encodeURIComponent(jobId) + '/files', true);
      self.setHeaders(xhr);
      xhr.onload = function () {
        self.loadingFiles = false;
        if (xhr.status === 200) {
          try {
            var data = JSON.parse(xhr.responseText);
            self.files = data.result || [];
          } catch (e) {
            self.files = [];
          }
        }
      };
      xhr.onerror = function () { self.loadingFiles = false; };
      xhr.send();
    },

    uploadToServer: function (file, type, callback) {
      var self = this;
      self.uploading = true;
      self.uploadProgress = 0;

      var formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);

      var xhr = new XMLHttpRequest();
      xhr.open('POST', self.baseUrl + 'steward-reports/' + encodeURIComponent(self.job.id) + '/upload', true);
      self.setHeaders(xhr);

      xhr.upload.onprogress = function (e) {
        if (e.lengthComputable) {
          self.uploadProgress = Math.round((e.loaded / e.total) * 100);
        }
      };

      xhr.onload = function () {
        self.uploading = false;
        self.uploadProgress = null;
        if (xhr.status === 200) {
          try {
            var resp = JSON.parse(xhr.responseText);
            if (resp.status_code === '0' || resp.status_code === 0) {
              alert('Upload error: ' + (resp.status_message || 'Unknown error'));
              if (callback) callback(false);
              return;
            }
          } catch (e) { /* not JSON, treat as success */ }
          self.loadFiles();
          if (callback) callback(true);
        } else {
          var msg = 'Upload failed (HTTP ' + xhr.status + ').';
          try {
            var err = JSON.parse(xhr.responseText);
            if (err.status_message) msg += ' ' + err.status_message;
          } catch (e) { /* ignore */ }
          alert(msg);
          if (callback) callback(false);
        }
      };

      xhr.onerror = function () {
        self.uploading = false;
        self.uploadProgress = null;
        alert('Upload failed — network error. Please try again.');
        if (callback) callback(false);
      };

      xhr.send(formData);
    },

    setHeaders: function (xhr) {
      // #1478 — guard against literal "undefined"/"null" context headers
      // (#1470 canonical role; see apiHeaders comment above).
      safeSetXhrHeaders(xhr, {
        'Authorization': this.userToken,
        'cs3-context-role': this.userCanonicalRole,
        'cs3-context-clientId': this.userClientID,
        'cs3-context-employeeId': this.employeeId,
      });
    },

    makeFilename: function (ext) {
      var now = new Date();
      var ts = now.getFullYear() +
        ('0' + (now.getMonth() + 1)).slice(-2) +
        ('0' + now.getDate()).slice(-2) + '_' +
        ('0' + now.getHours()).slice(-2) +
        ('0' + now.getMinutes()).slice(-2) +
        ('0' + now.getSeconds()).slice(-2);
      return this.job.job_number + '-' + this.stewardName.replace(/\s+/g, '_') + '_' + ts + ext;
    },

    // ──────────── AUDIO ────────────

    startAudio: function () {
      var self = this;
      self.view = 'audio';
      navigator.mediaDevices.enumerateDevices().then(function (devices) {
        self.audioDevices = devices.filter(function (d) { return d.kind === 'audioinput'; });
        if (self.audioDevices.length && !self.selectedAudioDevice) {
          self.selectedAudioDevice = self.audioDevices[0].deviceId;
        }
      }).catch(function () {
        self.audioDevices = [];
      });
    },

    startRecording: function () {
      var self = this;
      var constraints = { audio: self.selectedAudioDevice ? { deviceId: { exact: self.selectedAudioDevice } } : true };

      navigator.mediaDevices.getUserMedia(constraints).then(function (stream) {
        self.audioStream = stream;
        self.audioChunks = [];
        self.audioRecording = true;
        self.recordingSeconds = 0;

        self.recordingTimer = setInterval(function () {
          self.recordingSeconds++;
        }, 1000);

        self.mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
        self.mediaRecorder.ondataavailable = function (e) {
          if (e.data && e.data.size > 0) self.audioChunks.push(e.data);
        };
        self.mediaRecorder.onstop = function () {
          var blob = new Blob(self.audioChunks, { type: 'audio/webm' });
          var file = new File([blob], self.makeFilename('.webm'), { type: 'audio/webm' });
          self.cleanupMedia();
          self.view = 'home';
          self.uploadToServer(file, 'audio');
        };
        self.mediaRecorder.start(1000);

        // Waveform visualization
        self.setupWaveform(stream);
      }).catch(function (err) {
        alert('Could not access microphone: ' + err.message);
      });
    },

    stopRecording: function () {
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop();
      }
    },

    setupWaveform: function (stream) {
      var self = this;
      try {
        var audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        var source = audioCtx.createMediaStreamSource(stream);
        var analyser = audioCtx.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        self.audioAnalyser = analyser;

        var draw = function () {
          if (!self.audioRecording) return;
          self.waveAnimFrame = requestAnimationFrame(draw);
          var canvas = self.$refs.waveCanvas;
          if (!canvas) return;
          var ctx = canvas.getContext('2d');
          var bufferLength = analyser.frequencyBinCount;
          var dataArray = new Uint8Array(bufferLength);
          analyser.getByteTimeDomainData(dataArray);

          ctx.fillStyle = '#f5f5f5';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.lineWidth = 2;
          ctx.strokeStyle = '#27ae60';
          ctx.beginPath();

          var sliceWidth = canvas.width / bufferLength;
          var x = 0;
          for (var i = 0; i < bufferLength; i++) {
            var v = dataArray[i] / 128.0;
            var y = (v * canvas.height) / 2;
            if (i === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
            x += sliceWidth;
          }
          ctx.lineTo(canvas.width, canvas.height / 2);
          ctx.stroke();
        };
        draw();
      } catch (e) {
        // Web Audio not available, skip visualization
      }
    },

    // ──────────── CAMERA – PHOTO ────────────

    startCamera: function (mode) {
      var self = this;
      self.capturedPhotoUrl = null;
      self.capturedPhotoBlob = null;

      var constraints = mode === 'video'
        ? { video: true, audio: true }
        : { video: true };

      navigator.mediaDevices.getUserMedia(constraints).then(function (stream) {
        self.cameraStream = stream;
        if (mode === 'photo') {
          self.view = 'camera-photo';
          self.$nextTick(function () {
            self.attachStream('cameraVideo', stream);
          });
        } else {
          self.view = 'camera-video';
          self.$nextTick(function () {
            self.attachStream('cameraVideoRec', stream, function () {
              self.startOverlayLoop();
            });
          });
        }
      }).catch(function (err) {
        alert('Could not access camera: ' + err.message);
      });
    },

    attachStream: function (refName, stream, onPlaying) {
      var self = this;
      var attempts = 0;
      var tryAttach = function () {
        var el = self.$refs[refName];
        if (el) {
          el.srcObject = stream;
          el.onloadedmetadata = function () {
            el.play().then(function () {
              if (onPlaying) onPlaying();
            }).catch(function () {
              if (onPlaying) onPlaying();
            });
          };
        } else if (attempts < 10) {
          attempts++;
          setTimeout(tryAttach, 100);
        }
      };
      tryAttach();
    },

    capturePhoto: function () {
      var self = this;
      var video = self.$refs.cameraVideo;
      var canvas = self.$refs.photoCanvas;
      if (!video || !canvas) return;

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      var ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);

      // Burn metadata overlay: date/time + username
      var now = new Date();
      var ts = ('0' + (now.getMonth() + 1)).slice(-2) + '/' +
        ('0' + now.getDate()).slice(-2) + '/' +
        now.getFullYear() + ' ' +
        ('0' + now.getHours()).slice(-2) + ':' +
        ('0' + now.getMinutes()).slice(-2) + ':' +
        ('0' + now.getSeconds()).slice(-2);
      var name = self.stewardName || 'Unknown';
      var jobLabel = '#' + (self.job.job_number || '') + ' ' + (self.job.title || '');
      var lines = [ts, name, jobLabel];

      var fontSize = Math.max(14, Math.round(canvas.width / 40));
      ctx.font = 'bold ' + fontSize + 'px Arial, sans-serif';
      var lineHeight = fontSize * 1.3;
      var padding = Math.round(fontSize * 0.6);

      // Measure widest line for background
      var maxW = 0;
      lines.forEach(function (l) {
        var w = ctx.measureText(l).width;
        if (w > maxW) maxW = w;
      });

      // Draw semi-transparent background at bottom-left
      var bgH = lineHeight * lines.length + padding * 2;
      var bgW = maxW + padding * 2;
      var bgY = canvas.height - bgH - padding;
      ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
      ctx.fillRect(padding, bgY, bgW, bgH);

      // Draw text lines
      ctx.fillStyle = '#ffffff';
      ctx.textBaseline = 'top';
      lines.forEach(function (line, i) {
        ctx.fillText(line, padding * 2, bgY + padding + i * lineHeight);
      });

      canvas.toBlob(function (blob) {
        self.capturedPhotoBlob = blob;
        self.capturedPhotoUrl = window.URL.createObjectURL(blob);
        // Stop camera preview after capture
        if (self.cameraStream) {
          self.cameraStream.getTracks().forEach(function (t) { t.stop(); });
        }
      }, 'image/png');
    },

    retakePhoto: function () {
      var self = this;
      if (self.capturedPhotoUrl) {
        window.URL.revokeObjectURL(self.capturedPhotoUrl);
      }
      self.capturedPhotoUrl = null;
      self.capturedPhotoBlob = null;
      // Restart camera
      navigator.mediaDevices.getUserMedia({ video: true }).then(function (stream) {
        self.cameraStream = stream;
        self.attachStream('cameraVideo', stream);
      }).catch(function () { /* noop */ });
    },

    uploadCapturedPhoto: function () {
      var self = this;
      if (!self.capturedPhotoBlob) return;
      var file = new File([self.capturedPhotoBlob], self.makeFilename('.png'), { type: 'image/png' });
      if (self.capturedPhotoUrl) {
        window.URL.revokeObjectURL(self.capturedPhotoUrl);
        self.capturedPhotoUrl = null;
      }
      self.view = 'home';
      self.uploadToServer(file, 'photo');
    },

    // ──────────── CAMERA – VIDEO ────────────

    startOverlayLoop: function () {
      var self = this;
      var video = self.$refs.cameraVideoRec;
      var canvas = self.$refs.videoOverlayCanvas;
      if (!video || !canvas) return;

      // Use selected quality resolution for canvas
      var q = self.selectedQuality;
      var CW = q.width;
      var CH = q.height;
      canvas.width = CW;
      canvas.height = CH;

      var draw = function () {
        if (self.view !== 'camera-video') return;
        self.overlayAnimFrame = requestAnimationFrame(draw);

        var ctx = canvas.getContext('2d');
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, CW, CH);

        // Fit video into 4:3 canvas (letterbox if needed)
        if (video.videoWidth && video.videoHeight) {
          var vw = video.videoWidth;
          var vh = video.videoHeight;
          var scale = Math.min(CW / vw, CH / vh);
          var dw = vw * scale;
          var dh = vh * scale;
          var dx = (CW - dw) / 2;
          var dy = (CH - dh) / 2;
          ctx.drawImage(video, 0, 0, vw, vh, dx, dy, dw, dh);
        }

        // Overlay bar
        var barHeight = Math.max(28, Math.round(CH / 15));
        var fontSize = Math.max(14, Math.round(CH / 30));
        ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
        ctx.fillRect(0, CH - barHeight, CW, barHeight);
        ctx.fillStyle = '#ffffff';
        ctx.font = fontSize + 'px sans-serif';
        var now = new Date();
        var dateStr = ('0' + (now.getMonth() + 1)).slice(-2) + '/' +
          ('0' + now.getDate()).slice(-2) + '/' +
          now.getFullYear() + ' ' +
          ('0' + now.getHours()).slice(-2) + ':' +
          ('0' + now.getMinutes()).slice(-2) + ':' +
          ('0' + now.getSeconds()).slice(-2);
        var overlayText = self.job.job_number + '-' + self.stewardName + ', ' + dateStr;
        ctx.fillText(overlayText, 10, CH - 12);
      };
      draw();
    },

    startVideoRecording: function () {
      var self = this;
      var canvas = self.$refs.videoOverlayCanvas;
      if (!canvas) return;

      // Re-init canvas to selected quality (in case user changed dropdown)
      var q = self.selectedQuality;
      canvas.width = q.width;
      canvas.height = q.height;
      self.startOverlayLoop();

      self.videoChunks = [];
      self.videoRecordingBytes = 0;
      self.videoRecording = true;
      self.recordingSeconds = 0;

      self.recordingTimer = setInterval(function () {
        self.recordingSeconds++;
        // Auto-stop when time limit reached
        if (self.recordingSeconds >= self.videoMaxSeconds) {
          self.stopVideoRecording();
        }
      }, 1000);

      // Composite stream: canvas video + original audio
      var canvasStream = canvas.captureStream(30);
      var compositeStream = new MediaStream();

      canvasStream.getVideoTracks().forEach(function (t) { compositeStream.addTrack(t); });
      if (self.cameraStream) {
        self.cameraStream.getAudioTracks().forEach(function (t) { compositeStream.addTrack(t); });
      }

      var recOptions = { mimeType: 'video/webm' };
      if (q.bps) recOptions.videoBitsPerSecond = q.bps;

      self.videoMediaRecorder = new MediaRecorder(compositeStream, recOptions);
      self.videoMediaRecorder.ondataavailable = function (e) {
        if (e.data && e.data.size > 0) {
          self.videoChunks.push(e.data);
          self.videoRecordingBytes += e.data.size;
          // Auto-stop if approaching file size limit (leave 0.5 MB margin)
          if (self.videoRecordingBytes >= (q.maxSizeMb - 0.5) * 1024 * 1024) {
            self.stopVideoRecording();
          }
        }
      };
      self.videoMediaRecorder.onstop = function () {
        if (!self.videoChunks.length) {
          alert('Recording failed — no video data captured.');
          self.cleanupMedia();
          self.view = 'home';
          return;
        }
        var blob = new Blob(self.videoChunks, { type: 'video/webm' });
        var file = new File([blob], self.makeFilename('.webm'), { type: 'video/webm' });
        self.cleanupMedia();
        self.view = 'home';
        self.uploadToServer(file, 'video');
      };
      self.videoMediaRecorder.start(1000);
    },

    stopVideoRecording: function () {
      if (this.videoMediaRecorder && this.videoMediaRecorder.state !== 'inactive') {
        this.videoMediaRecorder.stop();
      }
    },

    // ──────────── TEXT EDITOR ────────────

    startTextEditor: function () {
      var self = this;
      self.editingFile = null;
      self.view = 'editor';
      self.$nextTick(function () {
        if (typeof CKEDITOR !== 'undefined') {
          self.editorInstance = CKEDITOR.replace('sr-editor', ckEditorConfig());
          self.editorInstance.on('instanceReady', function () {
            self.editorInstance.setData(self.buildTemplate());
          });
        }
      });
    },

    buildTemplate: function () {
      var job = this.job;
      var now = new Date();
      var date = ('0' + (now.getMonth() + 1)).slice(-2) + '/' + ('0' + now.getDate()).slice(-2) + '/' + now.getFullYear();
      var time = ('0' + now.getHours()).slice(-2) + ':' + ('0' + now.getMinutes()).slice(-2);

      var employerName = (job.employer) ? job.employer.name : 'NA';
      var employerContact = '';
      if (job.employer && job.employer.employer_users && job.employer.employer_users.length) {
        for (var i = 0; i < job.employer.employer_users.length; i++) {
          var eu = job.employer.employer_users[i];
          if (eu.user && eu.user.id === job.employer_user_id) {
            employerContact = eu.user.first_name + ' ' + eu.user.last_name;
            break;
          }
        }
      }
      var venueName = (job.venue) ? job.venue.name : 'NA';

      return '<p><strong><span style="font-size:12pt;">' +
        'Job #' + job.job_number + ' - ' + (job.title || '') + '<br />' +
        'Date: ' + date + ' &mdash; Time: ' + time + '<br />' +
        'Employer: ' + employerName + '<br />' +
        'Employer Contact: ' + (employerContact || 'NA') + '<br />' +
        'Venue: ' + venueName + '<br />' +
        'Steward: ' + this.stewardName +
        '</span></strong></p>' +
        '<hr />' +
        '<p><span style="font-size:12pt;">&nbsp;</span></p>';
    },

    saveDocument: function (saveAs) {
      var self = this;
      if (!self.editorInstance) return;

      var html = self.editorInstance.getData();

      // If editing an existing file (and not Save As), update in place
      if (self.editingFile && !saveAs) {
        self.uploading = true;

        // Send content as a file attachment; update ID via header (WAF strips form fields)
        // Include updated timestamp in filename
        var baseName = (self.editingFile.name || 'update.html').replace(/(_\d{8}_\d{6})?\.html?$/i, '');
        var updatedName = baseName + '_' + self.makeTimestamp() + '.html';
        var blob = new Blob([html], { type: 'text/html' });
        var formData = new FormData();
        formData.append('file', blob, updatedName);
        formData.append('type', 'document');

        var xhr = new XMLHttpRequest();
        xhr.open('POST', self.baseUrl + 'steward-reports/' + encodeURIComponent(self.job.id) + '/upload', true);
        self.setHeaders(xhr);
        xhr.setRequestHeader('X-CS-Update-Id', self.editingFile.id);

        xhr.onload = function () {
          self.uploading = false;
          if (xhr.status === 200) {
            try {
              var resp = JSON.parse(xhr.responseText);
              if (resp.status_code === '1') {
                self.cancelEditor();
                self.loadFiles();
                return;
              }
            } catch (e) { /* fall through */ }
            alert('Failed to save. Please try again.');
          } else {
            alert('Failed to save (HTTP ' + xhr.status + '). Please try again.');
          }
        };
        xhr.onerror = function () {
          self.uploading = false;
          alert('Failed to save. Please try again.');
        };
        xhr.send(formData);
        return;
      }

      // New file or Save As
      var defaultName = self.job.job_number + '-' + (self.job.title || 'Report').replace(/\s+/g, '_') + '_Report_' + self.makeTimestamp() + '.html';
      var filename = defaultName;

      if (saveAs) {
        var input = prompt('Enter filename:', defaultName);
        if (!input) return;
        filename = input;
        if (filename.indexOf('.html') === -1) filename += '.html';
      }

      var blob = new Blob([html], { type: 'text/html' });
      var file = new File([blob], filename, { type: 'text/html' });

      self.cancelEditor();
      self.uploadToServer(file, 'document');
    },

    cancelEditor: function () {
      if (this.editorInstance) {
        try { this.editorInstance.destroy(); } catch (e) { /* noop */ }
        this.editorInstance = null;
      }
      this.editingFile = null;
      this.view = 'home';
    },

    makeTimestamp: function () {
      var now = new Date();
      return now.getFullYear() +
        ('0' + (now.getMonth() + 1)).slice(-2) +
        ('0' + now.getDate()).slice(-2) + '_' +
        ('0' + now.getHours()).slice(-2) +
        ('0' + now.getMinutes()).slice(-2) +
        ('0' + now.getSeconds()).slice(-2);
    },

    // ──────────── EDIT EXISTING FILE ────────────

    isEditableFile: function (f) {
      if (f.type === 'document') return true;
      var mime = (f.mime_type || '').toLowerCase();
      if (mime.indexOf('text/') === 0) return true;
      var ext = (f.name || '').split('.').pop().toLowerCase();
      return ['txt', 'md', 'log', 'html', 'htm', 'csv', 'json', 'xml', 'css', 'js'].indexOf(ext) !== -1;
    },

    editExistingFile: function (f) {
      var self = this;
      self.editingFile = f;
      self.view = 'editor';

      // Load content from Drive first, then init editor with it
      var xhr = new XMLHttpRequest();
      xhr.open('GET', self.baseUrl + 'steward-reports/files/' + encodeURIComponent(f.id) + '/preview', true);
      xhr.responseType = 'text';
      self.setHeaders(xhr);

      xhr.onload = function () {
        var html = (xhr.status === 200) ? xhr.response : '<p>Failed to load file content.</p>';
        self.$nextTick(function () {
          if (typeof CKEDITOR !== 'undefined') {
            self.editorInstance = CKEDITOR.replace('sr-editor', ckEditorConfig());
            self.editorInstance.on('instanceReady', function () {
              self.editorInstance.setData(html);
            });
          }
        });
      };
      xhr.onerror = function () {
        self.$nextTick(function () {
          if (typeof CKEDITOR !== 'undefined') {
            self.editorInstance = CKEDITOR.replace('sr-editor', ckEditorConfig());
            self.editorInstance.on('instanceReady', function () {
              self.editorInstance.setData('<p>Failed to load file content.</p>');
            });
          }
        });
      };
      xhr.send();
    },

    // ──────────── FILE UPLOAD ────────────

    triggerFileUpload: function () {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click();
      }
    },

    handleFileUpload: function (e) {
      var self = this;
      var files = e.target.files;
      if (!files || !files.length) return;

      var uploadNext = function (index) {
        if (index >= files.length) return;
        self.uploadToServer(files[index], 'upload', function () {
          uploadNext(index + 1);
        });
      };
      uploadNext(0);

      // Reset input so same file can be re-selected
      if (self.$refs.fileInput) {
        self.$refs.fileInput.value = '';
      }
    },

    // ──────────── PREVIEW ────────────

    previewFile: function (f) {
      var self = this;
      self.previewingFile = f;
      self.previewBlobUrl = null;
      self.previewTextContent = null;
      self.previewLoading = true;
      self.previewError = false;
      self.view = 'preview';

      var isTextMime = f.mime_type && f.mime_type.indexOf('text/') === 0;
      var isHtmlFile = f.name && f.name.match(/\.html?$/i);
      var showAsText = isTextMime && !isHtmlFile;

      var xhr = new XMLHttpRequest();
      xhr.open('GET', self.baseUrl + 'steward-reports/files/' + encodeURIComponent(f.id) + '/preview', true);
      xhr.responseType = showAsText ? 'text' : 'blob';
      self.setHeaders(xhr);

      xhr.onload = function () {
        self.previewLoading = false;
        if (xhr.status === 200) {
          if (showAsText) {
            self.previewTextContent = xhr.response;
            self.previewBlobUrl = window.URL.createObjectURL(new Blob([xhr.response], { type: f.mime_type }));
          } else if (isHtmlFile && f.mime_type !== 'text/html') {
            // HTML file stored with wrong mime — re-wrap as text/html for iframe
            self.previewBlobUrl = window.URL.createObjectURL(new Blob([xhr.response], { type: 'text/html' }));
          } else {
            self.previewBlobUrl = window.URL.createObjectURL(xhr.response);
          }
        } else {
          self.previewError = true;
        }
      };
      xhr.onerror = function () {
        self.previewLoading = false;
        self.previewError = true;
      };
      xhr.send();
    },

    closePreview: function () {
      if (this.previewBlobUrl) {
        window.URL.revokeObjectURL(this.previewBlobUrl);
        this.previewBlobUrl = null;
      }
      this.previewingFile = null;
      this.view = 'home';
    },

    // ──────────── UTILS ────────────

    cancelMedia: function () {
      this.cleanupMedia();
      this.view = 'home';
    },

    cleanupMedia: function () {
      // Stop recording timer
      if (this.recordingTimer) {
        clearInterval(this.recordingTimer);
        this.recordingTimer = null;
      }
      this.recordingSeconds = 0;

      // Audio
      this.audioRecording = false;
      if (this.waveAnimFrame) {
        cancelAnimationFrame(this.waveAnimFrame);
        this.waveAnimFrame = null;
      }
      if (this.audioStream) {
        this.audioStream.getTracks().forEach(function (t) { t.stop(); });
        this.audioStream = null;
      }
      this.mediaRecorder = null;

      // Video
      this.videoRecording = false;
      if (this.overlayAnimFrame) {
        cancelAnimationFrame(this.overlayAnimFrame);
        this.overlayAnimFrame = null;
      }
      if (this.cameraStream) {
        this.cameraStream.getTracks().forEach(function (t) { t.stop(); });
        this.cameraStream = null;
      }
      this.videoMediaRecorder = null;

      // Photo
      if (this.capturedPhotoUrl) {
        window.URL.revokeObjectURL(this.capturedPhotoUrl);
        this.capturedPhotoUrl = null;
      }
      this.capturedPhotoBlob = null;
    },

    close: function () {
      this.cleanupMedia();
      this.$modal.hide('steward-report');
      this.$emit('close');
    },

    typeIcon: function (type) {
      var map = {
        audio: 'fa fa-microphone',
        video: 'fa fa-video-camera',
        photo: 'fa fa-camera',
        document: 'fa fa-file-text-o',
        upload: 'fa fa-file-o',
      };
      return map[type] || 'fa fa-file-o';
    },

    formatDate: function (dateStr) {
      if (!dateStr) return '';
      var d = new Date(dateStr);
      return ('0' + (d.getMonth() + 1)).slice(-2) + '/' +
        ('0' + d.getDate()).slice(-2) + '/' +
        d.getFullYear() + ' ' +
        ('0' + d.getHours()).slice(-2) + ':' +
        ('0' + d.getMinutes()).slice(-2);
    },
  },
};
</script>

<style scoped>
.sr-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
  padding: 15px 0;
}
.sr-action-btn {
  min-width: 140px;
}
.sr-action-btn i {
  margin-right: 5px;
}
.sr-camera-container {
  background: #000;
  text-align: center;
  border-radius: 4px;
  overflow: hidden;
}
.sr-waveform {
  background: #f5f5f5;
  border-radius: 4px;
  padding: 10px;
  text-align: center;
}
.sr-preview-text {
  background: #f8f8f8;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 15px;
  max-height: 500px;
  overflow: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-size: 13px;
}
.sr-thead-dark th {
  background: #333;
  color: #fff;
  border-color: #444;
}
.sr-sortable {
  cursor: pointer;
  user-select: none;
}
.sr-sortable:hover {
  background: #444;
}
.sr-sortable i {
  margin-left: 4px;
}
.sr-btn-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
  margin-top: 10px;
}
.sr-editor-section {
  display: flex;
  flex-direction: column;
  max-height: 100%;
}
.sr-photo-review {
  padding: 10px;
  background: #f5f5f5;
  border-radius: 4px;
  margin-top: 10px;
}
.sr-photo-actions .c-solid-btn {
  min-width: 110px;
}
.sr-rec-status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  font-family: monospace;
}
.sr-rec-dot {
  color: #c0392b;
  animation: sr-blink 1s infinite;
}
@keyframes sr-blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.2; }
}
.sr-rec-sep {
  color: #ccc;
}
.c-solid-btn--orange {
  background: #e67e22 !important;
  border-color: #d35400 !important;
  color: #fff !important;
}
.c-solid-btn--orange:hover {
  background: #d35400 !important;
}
.c-solid-btn--red {
  background: #c0392b !important;
  border-color: #a93226 !important;
  color: #fff !important;
}
.c-solid-btn--red:hover {
  background: #a93226 !important;
}
</style>
<style>
/*
 * Camera / photo / video — fit to 50% of screen.
 * Works for landscape 16:9, portrait 9:16, and anything in between.
 */
.sr-media-fit {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
}
.sr-media-fit video,
.sr-media-fit img {
  max-width: 50vw;
  max-height: 50vh;
  width: auto;
  height: auto;
  object-fit: contain;
  background: #000;
  border-radius: 4px;
}
.sr-video-wrapper {
  position: relative;
  margin: 0 auto;
}
.sr-video-canvas {
  max-width: 50vw;
  max-height: 50vh;
  width: auto;
  height: auto;
  object-fit: contain;
  background: #000;
  border-radius: 4px;
}
/* On mobile, allow media to use more screen real estate */
@media (max-width: 820px) {
  .sr-media-fit video,
  .sr-media-fit img,
  .sr-video-canvas {
    max-width: 90vw;
    max-height: 50vh;
  }
}
.cke_button__inserttimestamp .cke_button_icon {
  background-image: none !important;
  font-family: FontAwesome !important;
  font-size: 16px !important;
  line-height: 16px !important;
  display: inline-block !important;
}
.cke_button__inserttimestamp .cke_button_icon::after {
  content: "\f017";
  color: #2e7d32;
  font-size: 16px;
}
</style>
